import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { auditService } from '../../services/auditService';
import { AuditListParams, AuditSummaryResponse } from '../../types/audit';
import { isAbortError } from '../../utils/abortError';

type RangePreset = '1h' | '24h' | '7d' | '30d';

interface Props {
  refreshKey?: number;
  /** Optional drill-down callback. When provided, dashboard widgets
   *  become clickable and fire this with a partial filter that the
   *  parent should merge into the Events-tab filter state. */
  onDrillDown?: (filters: Partial<AuditListParams>) => void;
}

const PRESETS: Array<{ key: RangePreset; hours: number }> = [
  { key: '1h', hours: 1 },
  { key: '24h', hours: 24 },
  { key: '7d', hours: 24 * 7 },
  { key: '30d', hours: 24 * 30 },
];

function rangeFor(preset: RangePreset): { from: string; to: string } {
  const now = new Date();
  const hours = PRESETS.find((p) => p.key === preset)?.hours ?? 24;
  const from = new Date(now.getTime() - hours * 3600 * 1000);
  return { from: from.toISOString(), to: now.toISOString() };
}

const BAR_TRACK_BG = 'var(--theme-bg-tertiary, #f3f4f6)';
const BAR_FILL_BG = 'var(--theme-accent, #3b82f6)';
const BAR_FILL_BG_FAILURE = '#dc2626';

const KpiTile: React.FC<{
  value: string;
  label: string;
  valueColor?: string;
  onClick?: () => void;
}> = ({ value, label, valueColor, onClick }) => {
  if (onClick) {
    return (
      <button
        type="button"
        className="audit-kpi audit-kpi-clickable"
        onClick={onClick}
      >
        <div className="audit-kpi-value" style={valueColor ? { color: valueColor } : undefined}>
          {value}
        </div>
        <div className="audit-kpi-label">{label}</div>
      </button>
    );
  }
  return (
    <div className="audit-kpi">
      <div className="audit-kpi-value" style={valueColor ? { color: valueColor } : undefined}>
        {value}
      </div>
      <div className="audit-kpi-label">{label}</div>
    </div>
  );
};

const TileBar: React.FC<{
  label: string;
  value: number;
  total: number;
  failure?: boolean;
  onClick?: () => void;
}> = ({ label, value, total, failure, onClick }) => {
  const pct = total > 0 ? Math.max(2, Math.round((value / total) * 100)) : 0;
  const Wrapper: React.ElementType = onClick ? 'button' : 'div';
  const wrapperProps = onClick
    ? {
        type: 'button' as const,
        onClick,
        className: 'audit-bar-row audit-bar-row-clickable',
      }
    : { className: 'audit-bar-row' };
  return (
    <Wrapper {...wrapperProps}>
      <div className="audit-bar-label" title={label}>{label}</div>
      <div className="audit-bar-track" style={{ background: BAR_TRACK_BG }}>
        <div
          className="audit-bar-fill"
          style={{
            width: `${pct}%`,
            background: failure ? BAR_FILL_BG_FAILURE : BAR_FILL_BG,
          }}
        />
      </div>
      <div className="audit-bar-count">{value.toLocaleString()}</div>
    </Wrapper>
  );
};

export const AuditDashboard: React.FC<Props> = ({ refreshKey, onDrillDown }) => {
  const { t, formatDateTime } = useI18n();
  const [preset, setPreset] = useState<RangePreset>('24h');
  const [excludeReads, setExcludeReads] = useState(true);
  const [authenticatedOnly, setAuthenticatedOnly] = useState(true);
  const [data, setData] = useState<AuditSummaryResponse | null>(null);
  // The {from,to} window actually used for the currently-displayed data.
  // Pinned at fetch time so drill-downs query the exact same window the chart
  // was built from, instead of recomputing `now` (which drifts every render).
  const [appliedRange, setAppliedRange] = useState<{ from: string; to: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAbortRef = useRef<AbortController | null>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const { from, to } = rangeFor(preset);
      const response = await auditService.getAuditSummary(
        {
          from_timestamp: from,
          to_timestamp: to,
          exclude_reads: excludeReads,
          authenticated_only: authenticatedOnly,
        },
        { signal },
      );
      setData(response);
      setAppliedRange({ from, to });
    } catch (err) {
      if (isAbortError(err)) return;
      console.error('[AuditDashboard] load failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to load summary');
      setData(null);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [preset, excludeReads, authenticatedOnly]);

  useEffect(() => {
    const ac = new AbortController();
    loadAbortRef.current = ac;
    load(ac.signal);
    return () => ac.abort();
  }, [load, refreshKey]);

  const handleRefresh = useCallback(() => {
    loadAbortRef.current?.abort();
    const ac = new AbortController();
    loadAbortRef.current = ac;
    load(ac.signal);
  }, [load]);

  const peakInTimeline = useMemo(() => {
    if (!data?.timeline?.length) return 0;
    return Math.max(...data.timeline.map((b) => b.count));
  }, [data]);

  const topActorPeak = useMemo(() => {
    if (!data?.top_actors?.length) return 0;
    return Math.max(...data.top_actors.map((a) => a.count));
  }, [data]);

  const topActionPeak = useMemo(() => {
    if (!data?.top_actions?.length) return 0;
    return Math.max(...data.top_actions.map((a) => a.count));
  }, [data]);

  const failureRate =
    data && data.totals.events > 0
      ? ((data.totals.failed / data.totals.events) * 100).toFixed(1)
      : '0.0';

  // Build a {from_timestamp, to_timestamp} pair from the active preset
  // so drill-downs preserve the user's time scope.
  const drillDownRange = useMemo<Partial<AuditListParams>>(() => {
    if (!appliedRange) return {};
    return {
      from_timestamp: appliedRange.from,
      to_timestamp: appliedRange.to,
      exclude_reads: excludeReads || undefined,
    };
  }, [appliedRange, excludeReads]);

  const drillDown = useCallback(
    (extra: Partial<AuditListParams>) => {
      if (!onDrillDown) return;
      onDrillDown({ ...drillDownRange, ...extra });
    },
    [onDrillDown, drillDownRange],
  );

  return (
    <div className="audit-dashboard" data-testid="audit-dashboard">
      <div className="audit-dashboard-toolbar">
        <div className="audit-range-presets" role="tablist">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              role="tab"
              aria-selected={preset === p.key}
              className={`audit-range-btn${preset === p.key ? ' active' : ''}`}
              onClick={() => setPreset(p.key)}
              data-testid={`audit-range-${p.key}`}
            >
              {t(`audit.dashboard.range.${p.key}`)}
            </button>
          ))}
        </div>
        <label className="audit-exclude-reads">
          <input
            type="checkbox"
            checked={excludeReads}
            onChange={(e) => setExcludeReads(e.target.checked)}
          />
          {t('audit.dashboard.excludeReads')}
        </label>
        <label
          className="audit-exclude-reads"
          title={t('audit.dashboard.authenticatedOnlyHint')}
        >
          <input
            type="checkbox"
            checked={authenticatedOnly}
            onChange={(e) => setAuthenticatedOnly(e.target.checked)}
            data-testid="audit-authenticated-only"
          />
          {t('audit.dashboard.authenticatedOnly')}
        </label>
        <button
          type="button"
          className="audit-refresh-btn"
          onClick={handleRefresh}
          disabled={loading}
        >
          {loading ? t('common.loading') : t('audit.dashboard.refresh')}
        </button>
      </div>

      {error && <div className="audit-error-banner">{error}</div>}

      {data && (
        <>
          <div className="audit-kpi-grid">
            <KpiTile
              value={data.totals.events.toLocaleString()}
              label={t('audit.dashboard.kpi.totalEvents')}
              onClick={onDrillDown ? () => drillDown({}) : undefined}
            />
            <KpiTile
              value={data.totals.failed.toLocaleString()}
              valueColor={BAR_FILL_BG_FAILURE}
              label={`${t('audit.dashboard.kpi.failed')} (${failureRate}%)`}
              onClick={onDrillDown ? () => drillDown({ status: 'failed' }) : undefined}
            />
            <KpiTile
              value={data.totals.denied.toLocaleString()}
              label={t('audit.dashboard.kpi.denied')}
              onClick={onDrillDown ? () => drillDown({ status: 'denied' }) : undefined}
            />
            <KpiTile
              value={data.totals.unique_actors.toLocaleString()}
              label={t('audit.dashboard.kpi.uniqueActors')}
            />
          </div>

          {authenticatedOnly && (data.totals.excluded_anonymous ?? 0) > 0 && (
            <div
              className="audit-anonymous-hidden"
              data-testid="audit-anonymous-hidden"
            >
              {t('audit.dashboard.anonymousHidden', {
                count: (data.totals.excluded_anonymous ?? 0).toLocaleString(),
              })}
            </div>
          )}

          <div className="audit-charts-grid">
            <section className="audit-card">
              <h3>{t('audit.dashboard.topActors')}</h3>
              {data.top_actors.length === 0 ? (
                <div className="audit-empty">{t('audit.dashboard.empty')}</div>
              ) : (
                <div className="audit-bars">
                  {data.top_actors.map((a) => (
                    <TileBar
                      key={a.actor_username}
                      label={a.actor_username}
                      value={a.count}
                      total={topActorPeak}
                      onClick={
                        onDrillDown
                          ? () => drillDown({ actor_username: a.actor_username })
                          : undefined
                      }
                    />
                  ))}
                </div>
              )}
            </section>

            <section className="audit-card">
              <h3>{t('audit.dashboard.topActions')}</h3>
              {data.top_actions.length === 0 ? (
                <div className="audit-empty">{t('audit.dashboard.empty')}</div>
              ) : (
                <div className="audit-bars">
                  {data.top_actions.map((a) => (
                    <TileBar
                      key={a.action}
                      label={a.action}
                      value={a.count}
                      total={topActionPeak}
                      onClick={
                        onDrillDown
                          ? () => drillDown({ action: a.action })
                          : undefined
                      }
                    />
                  ))}
                </div>
              )}
            </section>
          </div>

          <section className="audit-card">
            <h3>
              {t('audit.dashboard.timeline')} ·{' '}
              <span className="audit-bucket-hint">
                {t(`audit.dashboard.bucket.${data.range.bucket}`)}
              </span>
            </h3>
            {data.timeline.length === 0 ? (
              <div className="audit-empty">{t('audit.dashboard.empty')}</div>
            ) : (
              <div
                className="audit-timeline"
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${data.timeline.length}, minmax(8px, 1fr))`,
                  gap: 2,
                  alignItems: 'end',
                  height: 120,
                }}
              >
                {data.timeline.map((b) => {
                  const heightPct = peakInTimeline > 0
                    ? Math.max(4, Math.round((b.count / peakInTimeline) * 100))
                    : 0;
                  return (
                    <div
                      key={b.bucket}
                      className="audit-timeline-bar"
                      title={`${b.bucket}: ${b.count}`}
                      style={{
                        height: `${heightPct}%`,
                        background: BAR_FILL_BG,
                        borderRadius: '2px 2px 0 0',
                      }}
                    />
                  );
                })}
              </div>
            )}
          </section>

          <section className="audit-card">
            <h3>{t('audit.dashboard.recentFailures')}</h3>
            {data.recent_failures.length === 0 ? (
              <div className="audit-empty">{t('audit.dashboard.noFailures')}</div>
            ) : (
              <div className="audit-failures-table-wrapper">
              <table className="audit-failures-table">
                <thead>
                  <tr>
                    <th>{t('audit.table.timestamp')}</th>
                    <th>{t('audit.table.actor')}</th>
                    <th>{t('audit.table.action')}</th>
                    <th>{t('audit.table.resource')}</th>
                    <th>{t('audit.row.errorMessage')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_failures.map((f) => {
                    const onClick = onDrillDown
                      ? () =>
                          drillDown({
                            status: 'failed',
                            resource_type: f.resource_type,
                            resource_id: f.resource_id || undefined,
                            action: f.action,
                          })
                      : undefined;
                    return (
                      <tr
                        key={f.id}
                        className={onClick ? 'audit-failure-row-clickable' : undefined}
                        onClick={onClick}
                        tabIndex={onClick ? 0 : undefined}
                        role={onClick ? 'button' : undefined}
                        onKeyDown={
                          onClick
                            ? (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  onClick();
                                }
                              }
                            : undefined
                        }
                      >
                        <td>{f.timestamp ? formatDateTime(f.timestamp) : '—'}</td>
                        <td>{f.actor_username || t('audit.table.anonymous')}</td>
                        <td><code>{f.action}</code></td>
                        <td>
                          <code>{f.resource_type}</code>
                          {f.resource_id ? `:${f.resource_id}` : ''}
                        </td>
                        <td className="audit-failures-error" title={f.error_message || ''}>
                          {f.error_message || '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default AuditDashboard;
